import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { Handle, Position } from 'react-flow-renderer';
import { useParams } from 'react-router-dom';

import { accent, font, fontSize, neutral, shadow, text } from 'styles/revampTokens';

interface MapIndicatorNodeProperties {
  data: {
    direction: string;
    label: string;
    block_name: string;
    obj_name: string;
    index: number;
    obj_id: number;
    formula: string;
  };
}

/**
 * A second, fixed category colour rather than an accent-token — this badge
 * differentiates data categories (this block's own indicators vs. ones
 * pulled in from elsewhere), which is a data/legend distinction, not an
 * interactive control state. `accent` (purple) is reserved for the former;
 * this is the Figma map view's own second hue ("Primary-blue", 75644:18457).
 */
const OTHER_BLOCK_COLOR = '#3BA7FA';

// Handle dots — small and neutral, closer to the connector "plugs" in the
// Figma map view than react-flow's default blue squares.
const handleStyle = {
  width: 6,
  height: 6,
  background: neutral.borderStrong,
  border: 'none',
};

const MapIndicatorNode = ({ data }: MapIndicatorNodeProperties) => {
  let inputHandlePos: Position;
  let outputHandlePos: Position;
  const { blockId } = useParams();

  const isSameBlock = blockId === `${data.obj_id}`;

  if (data.direction === 'TB') {
    inputHandlePos = Position.Top;
    outputHandlePos = Position.Bottom;
  } else {
    inputHandlePos = Position.Left;
    outputHandlePos = Position.Right;
  }

  const formula = data?.formula?.replace(/["']/g, '') || '';

  return (
    <Box
      bg={neutral.surface}
      border='1px solid'
      borderColor={neutral.border}
      borderRadius='10px'
      boxShadow={shadow.panel}
      p='12px'
      w='160px'
    >
      <Handle type='target' position={inputHandlePos} style={handleStyle} />
      <Flex direction='column' gap='8px' alignItems='flex-start' w='100%'>
        <Tooltip placement='top' hasArrow arrowSize={15} fontSize='md' label={formula}>
          <Text
            fontFamily={font.body}
            fontSize={fontSize.label}
            lineHeight='16px'
            color={text.primary}
            title={data.label}
            aria-label={data.label}
            noOfLines={2}
          >
            {data.label}
          </Text>
        </Tooltip>

        <Flex
          bg={isSameBlock ? accent.solid : OTHER_BLOCK_COLOR}
          borderRadius='full'
          px='8px'
          py='4px'
          alignItems='center'
          justifyContent='center'
        >
          <Text
            fontFamily={font.body}
            fontSize='10px'
            lineHeight='normal'
            color='white'
            whiteSpace='nowrap'
            title={data.block_name}
            aria-label={data.obj_name}
          >
            {data.obj_name}
          </Text>
        </Flex>
      </Flex>
      <Handle type='source' position={outputHandlePos} style={handleStyle} />
    </Box>
  );
};
export default MapIndicatorNode;
